/**
 * Questionnaire template labels and the "can this be sent to a supplier" rule,
 * shared by every template picker so none of them shows a raw type slug or
 * offers an Audit Lite template.
 */
import type { QualificationTemplate, QuestionnaireType } from './api';

/** Every type the backend offers (`QualificationTemplate.TEMPLATE_TYPES`). */
const CURRENT_TYPE_LABELS: Record<QuestionnaireType, string> = {
  onboarding: 'Onboarding',
  standard: 'Standard',
  compliance: 'Compliance',
  audit: 'Audit',
  esg: 'ESG',
  quality: 'Quality',
  custom: 'Custom',
};

/**
 * Template type -> label. Includes the types older templates may still carry
 * (capability, financial, sustainability, security).
 */
export const QUESTIONNAIRE_TYPE_LABELS: Record<string, string> = {
  ...CURRENT_TYPE_LABELS,
  capability: 'Capability',
  financial: 'Financial',
  sustainability: 'Sustainability',
  security: 'Security',
};

/**
 * Human label for a template type.
 *
 * @param type - `template_type` slug
 * @returns the label, or the slug title-cased when it is unknown
 */
export function templateTypeLabel(type: string): string {
  const known = QUESTIONNAIRE_TYPE_LABELS[type];
  if (known) return known;
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * True for an Audit Lite template: completed by the customer's own auditor,
 * never sent to a supplier.
 *
 * @param template - any template carrying `respondent`
 */
export function isAuditTemplate(template: Pick<QualificationTemplate, 'respondent'>): boolean {
  return template.respondent === 'auditor';
}

/**
 * The templates that may be assigned to a supplier (audit templates removed).
 *
 * @param templates - templates from the API
 * @returns the same list without audit templates
 */
export function assignableTemplates<T extends Pick<QualificationTemplate, 'respondent'>>(templates: T[]): T[] {
  return templates.filter((t) => !isAuditTemplate(t));
}

// A supplier still being onboarded is named with its stage, so a buyer can
// tell a draft from an approved supplier (and can search "draft").
const ONBOARDING_LABEL: Record<string, string> = {
  draft: 'Draft',
  pending_initial: 'Pending Initial Approval',
  invited: 'Invited',
  due_diligence: 'Due Diligence',
};

/**
 * A supplier's name for a picker, with its onboarding stage when not yet approved.
 *
 * @param supplier - a `my_connections` row (stage only sent with `include_onboarding`)
 * @returns e.g. "Acme Ltd (Draft)"
 */
export function supplierPickerLabel(supplier: { name: string; onboarding_status?: string | null }): string {
  const stage = supplier.onboarding_status ? ONBOARDING_LABEL[supplier.onboarding_status] : undefined;
  return stage ? `${supplier.name} (${stage})` : supplier.name;
}
